import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PdMark from '../components/PdMark';
import { useAuth } from '../context/AuthContext';
import { questionnaireApi, profileApi, sarApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AddOrganisationWizard } from '../components/organisations/AddOrganisationWizard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Onboarding — a single light screen, not a form to get through. It used
 * to ask for every organisation, every date, ID documents and a
 * permission toggle before letting anyone see the app; now it asks for
 * one thing — add your first organisation, through the same guided
 * wizard used everywhere else — and hands the rest to the dashboard's
 * "To do" list, which asks for one more organisation at a time
 * (`components/dashboard/useNextOrgSuggestion.js`) and, once a request
 * is out, for the other optional things worth doing while waiting.
 *
 * If what was just added is a real, resolved organisation with an email
 * address on file — enough to actually send a request, not just a
 * placeholder or a placement under one — we offer to draft and send it
 * right away, same prompt as adding one from the Organisations page.
 * Otherwise, or once that's answered, finishing the wizard or choosing
 * "I'll do this later" marks onboarding complete
 * (`POST /questionnaire/skip` — same endpoint, it just flips a flag) and
 * lands on the dashboard.
 */
export const Onboarding = () => {
  const navigate = useNavigate();
  const { loadUser, user } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState(user?.date_of_birth || '');
  const [finishing, setFinishing] = useState(false);
  const [readyToSendOrg, setReadyToSendOrg] = useState(null);
  const [creatingSar, setCreatingSar] = useState(false);

  useEffect(() => {
    if (user?.onboarding_complete) {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.onboarding_complete, navigate]);

  // A pure invitee (has_own_data false — see routers/auth.py's register())
  // has nothing of their own to add an organisation for. They still went
  // through MFA setup and the recovery code just now; skip straight past
  // this nudge to the account picker, where their invitation is waiting.
  useEffect(() => {
    if (user && user.has_own_data === false && !user.onboarding_complete) {
      (async () => {
        try {
          await questionnaireApi.complete();
        } catch {
          // Best-effort — the dashboard's own "to do" list doesn't depend
          // on onboarding_complete either way.
        }
        navigate('/choose-account', { replace: true });
      })();
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.date_of_birth) setDateOfBirth(user.date_of_birth);
  }, [user?.date_of_birth]);

  // Shared by every exit from this page — saves the date of birth (if
  // given) and flips onboarding_complete. Doesn't navigate itself, since
  // the "send now?" path needs to land on the new request instead of the
  // dashboard.
  const completeOnboarding = async () => {
    if (dateOfBirth && dateOfBirth !== user?.date_of_birth) {
      await profileApi.update({ date_of_birth: dateOfBirth }).catch(() => {
        // Best-effort — losing the date of birth shouldn't block finishing.
      });
    }
    await questionnaireApi.complete();
    await loadUser();
  };

  const finish = async (successMessage) => {
    setFinishing(true);
    try {
      await completeOnboarding();
      if (successMessage) toast.success(successMessage);
      navigate('/dashboard');
    } catch {
      toast.error('Something went wrong finishing setup. Try again in a moment.');
      setFinishing(false);
    }
  };

  const handleAdded = (result) => {
    setShowWizard(false);
    const { kind, organisation } = result || {};
    // Enough to send: a real organisation (not a holder, not a placement
    // added under one already known) with an email address on file —
    // otherwise the request would land on the SAR page just asking for
    // that email before anything can be sent.
    if (kind === 'organisation' && organisation && !organisation.unresolved && organisation.email) {
      setReadyToSendOrg(organisation);
      return;
    }
    finish('Organisation added. You can add more any time.');
  };

  const handleSkip = () => finish();

  const declineSend = () => {
    const org = readyToSendOrg;
    setReadyToSendOrg(null);
    finish(org ? `${org.name} added. You can add more any time.` : undefined);
  };

  const handleCreateSar = async () => {
    const org = readyToSendOrg;
    if (!org) return;
    setReadyToSendOrg(null);
    setCreatingSar(true);
    try {
      const res = await sarApi.generate({ organisation_id: org.id, organisation_name: org.name });
      await completeOnboarding();
      navigate(`/sar/${res.data.id}`);
    } catch {
      // The organisation itself was added successfully — don't strand
      // them here over a failed request; finish and let them send it
      // from the organisation page instead.
      toast.error('The request could not be created. You can send it from the organisation page.');
      await finish(`${org.name} added. You can add more any time.`);
    } finally {
      setCreatingSar(false);
    }
  };

  // A foster-home add can name a respite carer along the way — persist it
  // the same as the full Organisations page does, so it isn't lost.
  const persistRespitePool = (pool) => {
    profileApi.update({ respite_carers: pool }).catch(() => {
      toast.error('The respite carer could not be saved.');
    });
  };

  return (
    <div className="min-h-screen bg-ground flex flex-col">
      <header className="px-4 sm:px-6 py-6">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <PdMark size={28} className="text-brand" />
        </div>
      </header>

      <main className="flex-1 flex items-center px-4 sm:px-6 pb-16">
        <div className="max-w-lg mx-auto w-full bg-surface rounded-xl p-7 sm:p-10 animate-enter-up">
          <h1 className="font-heading text-[32px] sm:text-[36px] font-bold tracking-[-0.035em] leading-[1.05] text-ink mb-3">
            Let's get your first organisation added
          </h1>
          <p className="text-[16px] text-ink-55 leading-relaxed mb-8">
            A council, a school, a GP surgery — anywhere that might hold records about you.
            Add one now, roughly is fine. You can add more whenever you're ready; we'll remind
            you along the way.
          </p>

          <div className="space-y-1.5 mb-8" data-testid="onboarding-dob-card">
            <Label htmlFor="onboarding-dob" className="text-[15px] font-semibold text-ink">
              When were you born?
            </Label>
            <p className="text-sm text-ink-55">
              Optional, but it helps us work out school years and spot things in your records
              that look wrong for your age at the time.
            </p>
            <Input
              id="onboarding-dob"
              type="date"
              value={dateOfBirth || ''}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="max-w-xs bg-ground border-ground mt-1"
              data-testid="onboarding-dob-input"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setShowWizard(true)}
              disabled={finishing}
              className="flex-1"
              data-testid="onboarding-start-btn"
            >
              Add your first organisation
            </Button>
            <Button
              variant="ghost"
              onClick={handleSkip}
              disabled={finishing}
              data-testid="onboarding-skip-btn"
            >
              {finishing ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Saving" /> : "I'll do this later"}
            </Button>
          </div>
        </div>
      </main>

      <AddOrganisationWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        dob={dateOfBirth}
        organisations={[]}
        respitePool={[]}
        onRespitePoolChange={persistRespitePool}
        onAdded={handleAdded}
      />

      {/* onOpenChange only clears state on any close (Cancel, Action, escape,
          overlay) — it never navigates itself, since Radix fires it for
          every one of those and Cancel/Action already call declineSend /
          handleCreateSar explicitly with their own navigation. */}
      <AlertDialog open={!!readyToSendOrg} onOpenChange={(open) => { if (!open) setReadyToSendOrg(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ask {readyToSendOrg?.name} for your records now?</AlertDialogTitle>
            <AlertDialogDescription>
              {readyToSendOrg?.name} has been added, with enough on file to send a request. We
              write the letter now — you can still check it before it goes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={declineSend} data-testid="onboarding-send-decline">
              Not now
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreateSar}
              disabled={creatingSar}
              data-testid="onboarding-send-confirm"
            >
              {creatingSar && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Onboarding;
